import React, { cloneElement, isValidElement, useMemo } from 'react';

import { CodeBlock } from '../../types';
import { MappedAnchorLine } from '../../types/utils';
import { Simple } from '../components/CodeBlock/Simple';
import { InteractiveTextProps } from '../components/InteractiveText';
import {
  getLinesFromHighlight,
  removeAnchorsFromCode,
  getHighlightFromAnchors,
  getAnchorsFromChildren,
  getAnchorsFromCode,
  getCodeBlockInformation,
} from '../utils/codeBlock';

interface TextMap {
  highlight: Array<string | number>;
  codeBlockIndex: number;
}

type CodeMap = Record<string, TextMap>;

export const useInteractiveLanguage = (
  children: React.ReactNode,
  uuid: string,
  columnLayout: boolean,
  codeBlockFiles?: string[],
): [
  codeBlocks: CodeBlock[],
  processedChildren: React.ReactNode,
  codeMap: CodeMap,
  files: string[],
] => {
  // a list of all highlight props from InteractiveText
  const anchors = getAnchorsFromChildren(children);

  const codeBlocks = React.Children.toArray(children).reduce(
    (acc: CodeBlock[], child) => {
      if (isValidElement(child) && child.props.originalType === 'pre') {
        const preNode = React.Children.toArray(child.props.children)[0];
        if (isValidElement(preNode)) {
          // get the code language of the code block
          const [lang, fallbackLanguage] = getCodeBlockInformation(
            preNode.props.className,
          );

          // get the title from the props of InteractiveLanguage or the code block itself
          const title =
            codeBlockFiles && codeBlockFiles[acc.length]
              ? codeBlockFiles[acc.length]
              : preNode.props.title;

          const rawCode = React.Children.toArray(
            preNode.props.children,
          )[0]?.toString();

          if (rawCode) {
            return [
              ...acc,
              {
                title,
                language: lang || fallbackLanguage,
                // rawCode is required for detecting the the code highlight areas
                rawCode,
                code: removeAnchorsFromCode(rawCode, anchors),
              },
            ];
          }
        }
        return acc;
      }

      return acc;
    },
    [],
  );

  const [processedChildren, codeMap] = useMemo(() => {
    const mappedAnchors: MappedAnchorLine[] = [];
    const tempCodeMap: CodeMap = {};

    codeBlocks.forEach(({ rawCode }, index) => {
      mappedAnchors.push(...getAnchorsFromCode(rawCode, anchors, index));
    });

    // attach a highlighId to the InteractiveText elements
    // add the relevant snippet for the mobile view
    // add an entry to the codeMap
    const tempProcessedChildren = React.Children.map(
      children,
      (child, index) => {
        // remove all code nodes from the children
        if (isValidElement(child) && child.props.originalType === 'pre') {
          return null;
        }

        try {
          // the child is an InteractiveText element with prop "highlight"
          if (
            isValidElement(child) &&
            (child?.props as Record<string, any>).mdxType ===
              'InteractiveText' &&
            (child.props as InteractiveTextProps).highlight
          ) {
            const {
              highlight: highlightProp,
              file: fileProp,
            }: {
              highlight: string | Array<string | number>;
              file?: number;
            } = child.props as InteractiveTextProps;

            let highlightRange: (string | number)[] = [];

            // the file value can either come from the props or be derived from the anchor if it is unique
            let file = fileProp;

            if (typeof highlightProp === 'string') {
              const anchor = mappedAnchors.find(({ highlight }) =>
                highlightProp.match(new RegExp(`^${highlight}$`)),
              );

              if (!anchor) {
                throw new Error(
                  `
IMGLY - The "highlight" value ${highlightProp} from an InteractiveText element could not be found in any provided code snippets. 
                `,
                );
              }

              // derive the file value from the anchor
              file = file ?? anchor?.file;

              // will return an array of values with a 'start-end' pattern
              highlightRange = getHighlightFromAnchors(
                highlightProp,
                mappedAnchors,
                file,
              );
            } else {
              if (typeof file !== 'number') {
                throw new Error(
                  `
IMGLY - The prop "file" is required on an InteractiveText element if the prop "highlight" is not a string.
Please make sure that the content of this page follows this rule.
                `,
                );
              }

              highlightRange = highlightProp;
            }

            const highlightId = index.toString();

            // get the highlighted code and insert it as a seperate code block below the text for the mobile view
            const chunk = getLinesFromHighlight(highlightRange);
            const snippet = codeBlocks[file].code
              .split(/\r?\n/)
              // minus one since the highlight index starts at 1 instead of 0
              .slice(chunk[0] - 1, chunk[chunk.length - 1])
              .join('\n');

            tempCodeMap[highlightId] = {
              highlight: highlightRange,
              codeBlockIndex: file,
            };

            return [
              cloneElement(
                child,
                {
                  // add values for scroll behavior to the InteractiveText element
                  highlightId,
                  'data-language': uuid,
                } as InteractiveTextProps,
                [
                  (child.props as InteractiveTextProps).children,
                  columnLayout && (
                    <Simple
                      key={`${file}-range-${highlightRange.join('-')}`}
                      language={codeBlocks[file].language}
                      title={codeBlocks[file].title}
                    >
                      {snippet}
                    </Simple>
                  ),
                ],
              ),
            ];
          }

          return child;
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error((error as Error).message);
          }
          return child;
        }
      },
    );

    return [tempProcessedChildren, tempCodeMap];
  }, [columnLayout]);

  return [
    codeBlocks,
    processedChildren,
    codeMap,
    codeBlocks.map(({ title }) => title || ''),
  ];
};
